import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { judgingApi } from '@/api/judging';
import { judgesApi, type AssignJudgeRequest } from '@/api/judges';
import { submissionKeys } from './useSubmissions';
import type { SubmitScoreRequest } from '@/api/types';

export const judgeAssignmentKeys = {
  all: ['judge-assignments'] as const,
  list: (competitionId: string) => [...judgeAssignmentKeys.all, competitionId] as const,
};

/** Judges currently assigned to a competition - GET /judging/competitions/{competitionId}/judges. */
export function useJudgesForCompetition(competitionId: string | undefined) {
  return useQuery({
    queryKey: judgeAssignmentKeys.list(competitionId ?? ''),
    queryFn: () => judgesApi.listForCompetition(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

/** Each judge's scoring progress in a competition (organizer view). Refreshes while the page is open so a running judging session can be watched. */
export function useJudgesProgress(competitionId: string | undefined) {
  return useQuery({
    queryKey: [...judgeAssignmentKeys.all, 'progress', competitionId ?? ''],
    queryFn: () => judgesApi.progress(competitionId as string),
    enabled: Boolean(competitionId),
    refetchInterval: 30_000,
  });
}

export function useAssignJudge(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: AssignJudgeRequest) => judgesApi.assign(competitionId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: judgeAssignmentKeys.list(competitionId) }),
  });
}

export function useRemoveJudge(competitionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (judgeId: string) => judgesApi.remove(competitionId, judgeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: judgeAssignmentKeys.list(competitionId) }),
  });
}

/** Competitions the current judge is assigned to (drives the Judge portal's competition picker). Distinct from hooks/useCompetitions.ts useMyCompetitions, which covers every relationship (manage/judge/participate), not just judging. */
export function useMyJudgingCompetitions() {
  return useQuery({
    queryKey: ['judging', 'my-competitions'],
    queryFn: () => judgingApi.listMyCompetitions(),
  });
}

/** The competitor's own results across all competitions. */
export function useMyResults() {
  return useQuery({
    queryKey: ['judging', 'my-results'],
    queryFn: () => judgingApi.getMyResults(),
  });
}

export function useRanking(competitionId: string | undefined) {
  return useQuery({
    queryKey: ['judging', 'ranking', competitionId ?? ''],
    queryFn: () => judgingApi.getRanking(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

/** How far the current judge has gotten through this competition's judgeable submissions. */
export function useJudgingProgress(competitionId: string | undefined) {
  return useQuery({
    queryKey: ['judging', 'progress', competitionId ?? ''],
    queryFn: () => judgingApi.getProgress(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

/** The calling judge's own scores in a competition, so the queue can mark which entries are already scored. */
export function useMyScores(competitionId: string | undefined) {
  return useQuery({
    queryKey: ['judging', 'my-scores', competitionId ?? ''],
    queryFn: () => judgingApi.getMyScores(competitionId as string),
    enabled: Boolean(competitionId),
  });
}

export function useSubmissionScores(submissionId: string | undefined) {
  return useQuery({
    queryKey: ['judging', 'scores', submissionId],
    queryFn: () => judgingApi.listScoresForSubmission(submissionId as string),
    enabled: Boolean(submissionId),
  });
}

export function useSubmitScore() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: SubmitScoreRequest) => judgingApi.submitScore(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['judging', 'scores', variables.submissionId] });
      queryClient.invalidateQueries({ queryKey: submissionKeys.detail(variables.submissionId) });
      queryClient.invalidateQueries({ queryKey: ['judging', 'progress'] });
      queryClient.invalidateQueries({ queryKey: ['judging', 'my-scores'] });
    },
  });
}
